import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CrossIcon } from './icons/NavIcons';
import { supabase } from '../config/supabase';

/**
 * Composant pour afficher un message quand aucun événement n'est trouvé
 * @param {Object} props
 * @param {boolean} props.isFloating - Si true, affiche le composant en version flottante (pour la carte)
 * @param {string} props.cityName - Nom de la ville
 * @param {string} props.postalCode - Code postal (fallback)
 * @param {string} props.communeInseeCode - Code INSEE de la commune (préféré)
 */
const NoEventsMessage = ({ isFloating = false, cityName = '', postalCode = '', communeInseeCode = '' }) => {
  const { t } = useTranslation();
  const [isInterested, setIsInterested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');

  // Priorité: prop `communeInseeCode` -> localStorage 'communeInseeCode' -> fallback `postalCode`
  const communeCode = communeInseeCode || localStorage.getItem('communeInseeCode') || postalCode;

  const containerClass = isFloating
    ? "bg-white rounded-lg shadow-xl p-6"
    : "max-w-2xl mx-auto";

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà voté pour ce code (INSEE ou postal)
    const checkIfAlreadyInterested = () => {
      const visitorId = localStorage.getItem('maploop_visitor_id');
      if (!visitorId || !communeCode) return;

      const key = `interested_${communeCode}`;
      const hasVoted = localStorage.getItem(key);
      if (hasVoted) {
        setIsInterested(true);
      }
    };

    checkIfAlreadyInterested();
  }, [communeCode]);

  const handleInterestClick = async () => {
    if (isInterested || isLoading) return;

    setIsLoading(true);

    try {
      // Récupérer ou créer le visitor_id
      let visitorId = localStorage.getItem('maploop_visitor_id');
      if (!visitorId) {
        visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('maploop_visitor_id', visitorId);
      }

      // Utiliser le code de commune INSEE (ou fallback)
      // communeCode est défini plus haut

      // Insérer dans Supabase: utiliser `code_commune_insee`
      const { error } = await supabase
        .from('interested')
        .insert([
          {
            code_commune_insee: communeCode,
            visitor_id: visitorId,
            email: email || null
          }
        ]);

      if (error) throw error;

      // Marquer comme voté dans localStorage
      const key = `interested_${communeCode}`;
      localStorage.setItem(key, 'true');
      setIsInterested(true);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      alert(t('eventList.errorMessage') || 'Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={containerClass}>
      <div className="flex flex-col items-center justify-center py-5">
          <CrossIcon className="w-16 h-16 mb-4 text-bleu1" strokeWidth="2" />
      </div>
      <h2 className="text-h1 text-center mb-4">{t('eventList.noEvents')}</h2>

        <div className="w-full space-y-4">
          <p className="text-sm text-gray-600 text-center">{t('eventList.helpUs')}</p>
          {cityName && communeCode && (
            <p className="text-sm text-gray-700 text-center">
              {t('eventList.interestedMessage', { cityName, postalCode: communeCode })}
            </p>
          )}
        </div>  
          
          
          <div className="flex flex-col gap-3 items-center pt-5">
            <div className="w-64 space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('eventList.emailPlaceholder')}
                disabled={isInterested || isLoading}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-bleu1 focus:ring-2 focus:ring-bleu1 focus:ring-opacity-20 outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
              />
              <p className="text-xs text-gray-500 text-center">
                {t('eventList.emailOptional')}
              </p>
            </div>
            
            <button
              onClick={handleInterestClick}
              disabled={isInterested || isLoading}
              className={`${
                isInterested
                  ? 'bg-green-500 text-white cursor-default'
                  : 'bg-[#2894D8] text-white hover:bg-[#136E9D]'
              } py-3 px-6 rounded-lg transition-colors inline-flex items-center justify-center font-medium w-64 disabled:opacity-75`}
            >
              {isInterested ? (
                <> {t('eventList.interestedConfirmed')}</>
              ) : isLoading ? (
                <> {t('common.loading') || 'Chargement...'}</>
              ) : (
                <> {t('eventList.interestedButton')}</>
              )}
            </button>
            
            <a
              href="mailto:contact@maploop.fr?subject=Demande%20d%27événements%20-%20Maploop"
              className="text-bleu1 hover:text-bleu3 underline text-sm font-medium transition-colors"
            >
              {t('eventList.contactMaploop')}
            </a>
          </div>
        </div>
      
  );
};

export default NoEventsMessage;
